/**
 * campaignApprovalsConstants.js
 *
 * Single source of truth for every hardcoded value used by the Campaign Approvals
 * page — account managers, domain enums (actions, statuses, timing modes,
 * sub-categories), option lists, pill colors, table configuration, UI sizing, API
 * identifiers, formats and magic numbers. Nothing in the page should hardcode these.
 */

// ============================ API ============================
export const CAMPAIGN_APPROVALS_MODULE = 'campaignApprovals';
export const CAMPAIGN_APPROVALS_METHODS = {
	list: 'list',
	approve: 'approve',
	reject: 'reject'
};

// Campaign list request.
export const LIST_INITIAL_PAGE = 0;
export const LIST_LIMIT = 200; // backend caps `limit` at 200

// ============================ Account managers / tabs ============================
export const ACCOUNT_MANAGERS = ['Faheem Khan', 'Fahad Khan'];

// ============================ Domain enums ============================
export const ACTIONS = { APPROVE: 'approve', REJECT: 'reject' };

export const APPROVAL_STATUS = { PENDING: 'pending', APPROVED: 'approved', REJECTED: 'rejected' };

export const CAMPAIGN_TYPES = { SCHEDULE: 'schedule', PUSH: 'push' };

// Timing choice when a past-scheduled campaign is approved.
export const TIMING_MODES = { INSTANT: 'instant', SCHEDULE: 'schedule' };

// internal_sub_category values sent on approve.
// NOTE: the "Utility" dropdown choice maps to STANDARD (a normal utility send is
// internally 'standard'); only "Offhour Promotional" sends OFFHOUR_PROMOTIONAL.
export const SUB_CATEGORIES = {
	OFFHOUR_PROMOTIONAL: 'offhour_promotional',
	STANDARD: 'standard'
};

// Template payload identifiers.
export const TEMPLATE_CATEGORY_UTILITY = 'UTILITY';
export const TEMPLATE_COMPONENT_BODY = 'BODY';

// ============================ Option lists ============================
export const ACTION_OPTIONS = [
	{ value: ACTIONS.APPROVE, label: 'Approve' },
	{ value: ACTIONS.REJECT, label: 'Reject' }
];

// Shown on approve only when a selected campaign's scheduled time is already in the past.
export const TIMING_OPTIONS = [
	{ value: TIMING_MODES.INSTANT, label: 'Instant (5 minutes)' },
	{ value: TIMING_MODES.SCHEDULE, label: 'Schedule' }
];

// internal_sub_category choices for UTILITY campaigns (non-utility → STANDARD).
// "Utility" (a normal send) resolves to STANDARD; only Offhour Promotional differs.
export const UTILITY_SUB_CATEGORY_OPTIONS = [
	{ value: SUB_CATEGORIES.STANDARD, label: 'Utility' },
	{ value: SUB_CATEGORIES.OFFHOUR_PROMOTIONAL, label: 'Offhour Promotional' }
];

// ============================ Timing ============================
export const INSTANT_DELAY_MINUTES = 5; // instant send = now + this many minutes
export const MINUTE_UNIT = 'minutes'; // moment().add() unit
export const DATETIME_LOCAL_FORMAT = 'YYYY-MM-DDTHH:mm'; // <input type="datetime-local"> value

// ============================ Colors ============================
// Semantic pill colors (text + background pairs).
export const COLORS = {
	successText: '#065F46',
	successBg: '#D1FAE5',
	dangerText: '#991B1B',
	dangerBg: '#FEE2E2',
	warningText: '#92400E',
	warningBg: '#FEF3C7',
	neutralText: '#374151',
	neutralBg: '#f3f4f6'
};

// Per-template-category pill palette.
export const CATEGORY_PILLS = {
	utility: { label: 'Utility', color: '#166534', bgColor: '#dcfce7' },
	marketing: { label: 'Marketing', color: '#7c2d12', bgColor: '#ffedd5' },
	authentication: { label: 'Authentication', color: '#0369a1', bgColor: '#e0f2fe' },
	service: { label: 'Service', color: '#3730a3', bgColor: '#e0e7ff' }
};

// Per-campaign-type pill palette.
export const TYPE_PILLS = {
	schedule: { label: 'Schedule', color: '#3730a3', bgColor: '#e0e7ff' },
	push: { label: 'Push', color: '#0369a1', bgColor: '#e0f2fe' }
};

// ============================ Table configuration ============================
export const TABLE_PER_PAGE = 10;
export const TABLE_ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];
export const TABLE_MAX_HEIGHT = '600px';

// Cell text truncation lengths.
export const TRUNCATE = { MESSAGE: 40 };

// ============================ UI sizing ============================
export const REVIEW_MODAL_WIDTH = 560;
export const INLINE_SPINNER_SIZE = 16;
export const NOTES_ROWS = 3;
// Inline style for the leading icon inside the toolbar button.
export const BUTTON_ICON_STYLE = { fontSize: 18, marginRight: 6 };
// Scroll cap for the campaign list inside the confirmation dialog.
export const CONFIRM_LIST_MAX_HEIGHT = 200;

// ============================ Swal dialog ============================
export const SWAL_CLASS = 'swal-wide';
export const SWAL_WIDTH = '450px';
